import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	MenuItem,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";

import globalAxios from "../../globalAxios.js";
import { SERVER_URL } from "../../config.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.ts";

import {
	selectPotentialRegularSessionsOpen,
	selectActivePotentialRegularSession,
	selectPotentialRegularSessions,
	selectPotentialRegularSessionsActiveIndex,
	closePotentialRegularSessionsPrompt,
	nextPotentialRegularSession,
	previousPotentialRegularSession,
	removePotentialRegularSessionAtIndex,
	clearPotentialRegularSessions,
} from "../../store/slices/potentialRegularSessionsSlice.ts";

import {
	setSnackbarOpen,
	setSnackbarSeverity,
	setSnackbarText,
} from "../../store/slices/snackbarSlice.ts";
import { fetchRegularSessions, fetchUserSessions } from "../../index.js";

const weekCycles = ["A", "B", "C", "D"];
const weekDays = [
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
	"Sunday",
];

const PotentialRegularSessionDialog = () => {
	const dispatch = useAppDispatch();

	const open = useAppSelector(selectPotentialRegularSessionsOpen);
	const activeSuggestion = useAppSelector(
		selectActivePotentialRegularSession,
	);
	const suggestions = useAppSelector(selectPotentialRegularSessions);
	const activeIndex = useAppSelector(
		selectPotentialRegularSessionsActiveIndex,
	);

	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [draft, setDraft] = useState<any>(null);

	useEffect(() => {
		if (activeSuggestion?.regularSessionDraft) {
			setDraft(activeSuggestion.regularSessionDraft);
			setEditing(false);
		}
	}, [activeSuggestion]);

	if (!activeSuggestion || !draft) return null;

	const hasMultipleSuggestions = suggestions.length > 1;

	const handleClose = () => {
		dispatch(closePotentialRegularSessionsPrompt());
	};

	const handleDismissCurrent = () => {
		dispatch(removePotentialRegularSessionAtIndex(activeIndex));
	};

	const handleDismissAll = () => {
		dispatch(clearPotentialRegularSessions());
	};

	const handleChange = (field: string, value: any) => {
		setDraft((prev: any) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleCreateRegularSession = async () => {
		try {
			setSaving(true);

			const sessionIdsToLink = Array.from(
				new Set(
					[
						activeSuggestion.currentSessionId,
						activeSuggestion.session_id,
						...(activeSuggestion.matchedSessionIds || []),
					].filter(Boolean),
				),
			);

			await globalAxios.post(SERVER_URL + "/regular-sessions", {
				...draft,
				sessionIdsToLink,
			});

			await fetchRegularSessions(dispatch);
			await fetchUserSessions(dispatch);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("success"));
			dispatch(setSnackbarText("Regular session created."));

			dispatch(removePotentialRegularSessionAtIndex(activeIndex));
		} catch (err) {
			console.error("[POTENTIAL REGULAR] create failed:", err);

			dispatch(setSnackbarOpen(true));
			dispatch(setSnackbarSeverity("error"));
			dispatch(setSnackbarText("Failed to create regular session."));
		} finally {
			setSaving(false);
		}
	};

	const renderField = (
		label: string,
		field: string,
		type: "text" | "select" | "switch" = "text",
		options: string[] = [],
	) => {
		if (!editing) {
			return (
				<Box>
					<Typography variant="caption" color="text.secondary">
						{label}
					</Typography>
					<Typography variant="body2">
						{typeof draft[field] === "boolean"
							? draft[field]
								? "Yes"
								: "No"
							: draft[field] || "—"}
					</Typography>
				</Box>
			);
		}

		if (type === "switch") {
			return (
				<FormControlLabel
					control={
						<Switch
							checked={Boolean(draft[field])}
							onChange={(e) =>
								handleChange(field, e.target.checked)
							}
						/>
					}
					label={label}
				/>
			);
		}

		if (type === "select") {
			return (
				<TextField
					select
					label={label}
					value={draft[field] || ""}
					onChange={(e) => handleChange(field, e.target.value)}
					size="small"
					fullWidth
				>
					{options.map((option) => (
						<MenuItem key={option} value={option}>
							{option}
						</MenuItem>
					))}
				</TextField>
			);
		}

		return (
			<TextField
				label={label}
				value={draft[field] || ""}
				onChange={(e) => handleChange(field, e.target.value)}
				size="small"
				fullWidth
			/>
		);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Potential Regular Session Detected</DialogTitle>

			<DialogContent>
				<Stack spacing={2}>
					<Typography variant="body2" color="text.secondary">
						This uploaded session appears to match a recurring work
						pattern.
					</Typography>

					{hasMultipleSuggestions && (
						<Typography variant="caption" color="text.secondary">
							Suggestion {activeIndex + 1} of {suggestions.length}
						</Typography>
					)}

					<Divider />

					<Stack spacing={1.5}>
						<Typography variant="subtitle2">
							Regular session details
						</Typography>
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								Surgeon
							</Typography>

							<Typography variant="body2">
								{draft.surgeon_name || "—"}
							</Typography>
						</Box>

						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								Hospital
							</Typography>

							<Typography variant="body2">
								{draft.hospital_name || "—"}
							</Typography>
						</Box>
						{renderField(
							"Week cycle",
							"week_cycle",
							"select",
							weekCycles,
						)}
						{renderField(
							"Week day",
							"week_day",
							"select",
							weekDays,
						)}
						{renderField("Start date", "start_date")}
						{renderField("End date", "end_date")}
						{renderField("Start time", "start_time")}
						{renderField("End time", "end_time")}

						<Stack direction="row" spacing={2}>
							{renderField("AM", "am", "switch")}
							{renderField("PM", "pm", "switch")}
							{renderField("TW", "tw", "switch")}
						</Stack>
					</Stack>

					<Divider />

					<Box>
						<Typography variant="subtitle2">
							Matched dates
						</Typography>

						<Stack component="ul" sx={{ pl: 3, mt: 1 }}>
							{Array.from(
								new Set(
									(
										activeSuggestion.matchedDates || []
									).filter(Boolean),
								),
							)
								.sort()
								.map((date) => (
									<Typography
										component="li"
										variant="body2"
										key={date}
									>
										{date}
									</Typography>
								))}
						</Stack>
					</Box>
				</Stack>
			</DialogContent>

			<DialogActions>
				{hasMultipleSuggestions && (
					<>
						<Button
							onClick={() =>
								dispatch(previousPotentialRegularSession())
							}
							disabled={saving}
						>
							Previous
						</Button>

						<Button
							onClick={() =>
								dispatch(nextPotentialRegularSession())
							}
							disabled={saving}
						>
							Next
						</Button>
					</>
				)}

				<Button onClick={handleDismissCurrent} disabled={saving}>
					Dismiss
				</Button>

				{hasMultipleSuggestions && (
					<Button onClick={handleDismissAll} disabled={saving}>
						Dismiss All
					</Button>
				)}

				{!editing ? (
					<Button
						variant="outlined"
						onClick={() => setEditing(true)}
						disabled={saving}
					>
						Edit
					</Button>
				) : (
					<Button
						variant="outlined"
						onClick={() => {
							setDraft(activeSuggestion.regularSessionDraft);
							setEditing(false);
						}}
						disabled={saving}
					>
						Cancel Edit
					</Button>
				)}

				<Button
					disableElevation
					variant="contained"
					onClick={handleCreateRegularSession}
					disabled={saving}
				>
					{saving ? "Saving..." : "Save Regular Session"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PotentialRegularSessionDialog;
